/**
 * source-workload-pane.js — Source workload config pane (right-side panel)
 *
 * Owns:
 *  - Pane state (active siteId + workloadId)
 *  - openSourceWorkloadPane(siteId, workloadId) — opens and renders the pane
 *  - closeSourceWorkloadPane() — closes pane, clears state
 *  - isSourceWorkloadPaneOpen() — boolean accessor for click-outside handling
 *  - getSourceWorkloadPaneState() — { siteId, workloadId } for external use
 *  - All delegated pane events: protection toggles, count inputs, server picker,
 *    group add/remove, close/done
 *
 * Step 1 — current state (descriptive):
 *   Protection flags on source workload groups describe what the customer HAS today,
 *   not what PS will implement. They never directly drive effort calculation.
 *   Step 2 scope matrix selections drive effort (prescriptive / future state).
 *
 * Protection flag auto-clear rules:
 *   sureBackup  auto-clears when backup     is turned off
 *   sureReplica auto-clears when replication is turned off
 */

import { sites, AppState } from './state.js';
import { getSiteById, buildDefaultSourceWorkloadJob } from './helpers.js';
import {
    sourceWorkloadLabels,
    replicationAllowedWorkloads,
    cdpAllowedWorkloads,
    sureBackupAllowedWorkloads,
    backupCopyExcludedWorkloads,
} from './workload-defs.js';
import { escapeHtml, getWorkloadQuantityLabel, getWorkloadQuantitySummary, isVb365ManagedWorkload, isVbfCloudManagedWorkload, getVbfCloudProvider, CLOUD_PROVIDER_CONFIGS, normalizeToggleMigratedCount } from './utils.js';
import { emit, BUS_EVENTS } from './bus.js';
import { buildSwpChipDetail, buildSwpChipBadges } from './recalc.js';

// ── DOM refs (resolved once on init) ─────────────────────────────────────────
let paneEl      = null;   // <aside id="swp-pane">
let paneBodyEl  = null;   // <div id="swp-pane-body">
let paneTitleEl = null;   // element showing workload label
let paneSubEl   = null;   // element showing site name + "· current state"
let paneIconEl  = null;   // element holding the workload icon

// ── Pane state ────────────────────────────────────────────────────────────────
let swPaneSiteId     = null;
let swPaneWorkloadId = null;
let _paneOpenedAt    = 0;    // timestamp of last open — used by click-outside guard
let _barDrag         = null; // { track, jobIndex, needsRerender } | null while dragging a bar

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Builds a list of VBR server options from site existingEnvironment slot data.
 * VBR instances are stored as count chips (not as site.veeamServers entries),
 * so we synthesise one entry per slot with a stable "ee-vbr-<slot>" id.
 */
function _getVbrServerOptions(site) {
    const ee       = site.existingEnvironment || {};
    const count    = normalizeToggleMigratedCount(ee.vbrServers);
    if (count === 0) return [];

    // Use the same 'env-vbr-{siteId}-{slot}' instanceId convention as
    // getS2ReuseableVbrs() / getSyntheticLegacyVbrsForSite() in helpers.js
    // so that managedByServerId values round-trip correctly into Step 2.
    const platforms = site.existingEnvironmentVbrPlatforms || {};
    const options   = [];
    for (let slot = 0; slot < count; slot++) {
        const plat  = platforms[slot] || 'windows';
        const label = plat === 'linux' ? `VBR VSA (slot ${slot + 1})` : `VBR Windows (slot ${slot + 1})`;
        options.push({ instanceId: `env-vbr-${site.id}-${slot}`, name: label });
    }
    return options;
}

/**
 * Builds a list of VB365 server options from named veeamServers across all sites.
 * VB365 is a centralised service — a single instance can manage M365 users across
 * multiple sites, so we collect from all sites rather than just the workload's site.
 */
function _getVb365ServerOptions(_site) {
    return sites
        .flatMap((s) => s.veeamServers || [])
        .filter((sv) => sv.type === 'vb365')
        .map((sv) => ({ instanceId: sv.instanceId, name: sv.name || 'VB365 Server' }));
}

/**
 * Builds a list of VBfCloud appliance options for the given workload value.
 * Matches the cloud provider (aws/azure/gcp) from CLOUD_PROVIDER_CONFIGS and
 * collects matching named server instances from all sites.
 */
function _getVbfCloudServerOptions(workloadValue) {
    const provider = getVbfCloudProvider(workloadValue);
    if (!provider) return [];
    const cfg = CLOUD_PROVIDER_CONFIGS[provider];
    const serverType = `vbf${provider}`;   // 'vbfaws' | 'vbfaz' | 'vbfgc'
    return sites
        .flatMap((s) => s.veeamServers || [])
        .filter((sv) => sv.type === serverType)
        .map((sv) => ({ instanceId: sv.instanceId, name: sv.name || cfg.serverLabel }));
}

// ── Public accessors ──────────────────────────────────────────────────────────

export function isSourceWorkloadPaneOpen() {
    return swPaneSiteId !== null;
}

export function getSourceWorkloadPaneState() {
    return { siteId: swPaneSiteId, workloadId: swPaneWorkloadId };
}

// ── Open / close ──────────────────────────────────────────────────────────────

/**
 * Opens the pane for a given source workload on a site.
 * If the pane is already open for the same workload, closes it (toggle).
 * @param {number} siteId
 * @param {number} workloadId
 */
export function openSourceWorkloadPane(siteId, workloadId) {
    // Toggle: clicking the same chip again closes the pane
    if (swPaneSiteId === siteId && swPaneWorkloadId === workloadId) {
        closeSourceWorkloadPane();
        return;
    }

    const site = getSiteById(siteId);
    if (!site) return;
    const workload = (site.sourceWorkloads || []).find((w) => w.id === workloadId);
    if (!workload) return;

    swPaneSiteId     = siteId;
    swPaneWorkloadId = workloadId;
    _paneOpenedAt    = Date.now();

    // Sanitise: clear any protection flags that aren't valid for this workload type.
    // Prevents stale state if workload type was changed after protection was set.
    _sanitiseWorkloadProtection(workload);

    _renderPane(site, workload);
    paneEl.hidden = false;
    paneEl.setAttribute('aria-hidden', 'false');

    // Highlight the active chip in the canvas
    _syncActiveChipState();
}

export function closeSourceWorkloadPane() {
    swPaneSiteId     = null;
    swPaneWorkloadId = null;
    _barDrag         = null;

    if (paneEl) {
        paneEl.hidden = true;
        paneEl.setAttribute('aria-hidden', 'true');
    }

    _syncActiveChipState();
}

// ── Render ────────────────────────────────────────────────────────────────────

function _renderPane(site, workload) {
    if (!paneEl) return;

    const label      = sourceWorkloadLabels[workload.workloadValue] || workload.workloadValue || 'Workload';
    const siteName   = _getSiteDisplayNameForPane(site);
    const isVb365    = isVb365ManagedWorkload(workload.workloadValue);
    const isVbfCloud = isVbfCloudManagedWorkload(workload.workloadValue);
    const servers    = isVb365   ? _getVb365ServerOptions(site)
                     : isVbfCloud ? _getVbfCloudServerOptions(workload.workloadValue)
                     : _getVbrServerOptions(site);

    // Update header
    if (paneTitleEl) paneTitleEl.textContent = label;
    if (paneSubEl)   paneSubEl.textContent   = `${siteName} · current state`;
    if (paneIconEl)  paneIconEl.innerHTML    = _buildWorkloadIconHtml(workload.workloadValue);

    // Render body
    if (paneBodyEl) paneBodyEl.innerHTML = _buildPaneBodyMarkup(workload, servers, isVb365, isVbfCloud);
}

/** Re-renders just the pane body, preserving header — used after mutations. */
function _rerenderPaneBody() {
    if (!swPaneSiteId || !swPaneWorkloadId) return;
    const site = getSiteById(swPaneSiteId);
    if (!site) return;
    const workload = (site.sourceWorkloads || []).find((w) => w.id === swPaneWorkloadId);
    if (!workload) { closeSourceWorkloadPane(); return; }

    const isVb365    = isVb365ManagedWorkload(workload.workloadValue);
    const isVbfCloud = isVbfCloudManagedWorkload(workload.workloadValue);
    const servers    = isVb365   ? _getVb365ServerOptions(site)
                     : isVbfCloud ? _getVbfCloudServerOptions(workload.workloadValue)
                     : _getVbrServerOptions(site);
    if (paneBodyEl) paneBodyEl.innerHTML = _buildPaneBodyMarkup(workload, servers, isVb365, isVbfCloud);
}

// ── HTML builders ─────────────────────────────────────────────────────────────

// ── Job type metadata ─────────────────────────────────────────────────────────

const _JOB_META = {
    backup:      { label: 'Backup',       cls: 'backup',      short: 'BKP' },
    replication: { label: 'Replication',  cls: 'replication', short: 'REP' },
    cdp:         { label: 'CDP',          cls: 'cdp',         short: 'CDP' },
    backupCopy:  { label: 'Backup copy',  cls: 'backupCopy',  short: 'CPY' },
    sureBackup:  { label: 'SureBackup',   cls: 'sure',        short: 'SBK', requires: 'backup' },
    sureReplica: { label: 'SureReplica',  cls: 'sureReplica', short: 'SRP', requires: 'replication' },
};

// ── Pane body builder ─────────────────────────────────────────────────────────

function _buildPaneBodyMarkup(workload, servers, isVb365 = false, isVbfCloud = false) {
    const wv         = workload.workloadValue;
    const unit       = getWorkloadQuantityLabel(wv);
    const totalCount = parseInt(workload.totalCount, 10) || 0;
    const jobs       = Array.isArray(workload.jobs) ? workload.jobs : [];

    const canReplicate  = replicationAllowedWorkloads.has(wv);
    const canCdp        = cdpAllowedWorkloads.has(wv);
    const canSureBackup = sureBackupAllowedWorkloads.has(wv);
    const canBackupCopy = !backupCopyExcludedWorkloads.has(wv);

    // Which primary types exist in the job list (gates SureBackup/SureReplica)
    const hasBackupJob      = jobs.some((j) => j.type === 'backup');
    const hasReplicationJob = jobs.some((j) => j.type === 'replication');

    const jobsHtml = jobs.map((job, i) =>
        _buildJobCardMarkup(job, i, totalCount, servers, jobs, isVb365, isVbfCloud)
    ).join('');

    const emptyHint = jobs.length === 0
        ? `<div class="swp-jobs-empty">
               <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3L20 7v5c0 5.25-3.6 10.2-8 11.95C7.6 22.2 4 17.25 4 12V7l8-4z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="16.5" x2="12.01" y2="16.5"/></svg>
               <p>No current Veeam jobs</p>
               <p class="swp-jobs-empty-sub">This workload will be treated as unprotected in Step 2</p>
           </div>`
        : '';

    // Type picker buttons
    const _typeBtn = (type, disabled, lockReason) => {
        const m = _JOB_META[type];
        return `<button type="button"
            class="swp-type-btn swp-type-btn--${m.cls}${disabled ? ' swp-type-btn--locked' : ''}"
            data-swp-add-job-type="${type}"
            ${disabled ? `disabled title="${escapeHtml(lockReason)}"` : ''}
            aria-label="Add ${m.label} job"
        >${escapeHtml(m.label)}</button>`;
    };

    const primaryBtns = [
        _typeBtn('backup',      false, ''),
        canReplicate  ? _typeBtn('replication', false, '') : '',
        canCdp        ? _typeBtn('cdp',         false, '') : '',
        canBackupCopy ? _typeBtn('backupCopy',  false, '') : '',
    ].filter(Boolean).join('');

    const sureBtns = [
        canSureBackup ? _typeBtn('sureBackup',  !hasBackupJob,      'Add a Backup job first')      : '',
        canReplicate  ? _typeBtn('sureReplica', !hasReplicationJob, 'Add a Replication job first') : '',
    ].filter(Boolean);

    const typePicker = `${primaryBtns}${sureBtns.length ? `</div><div class="swp-type-picker-sure-row"><span class="swp-type-picker-sure-label">Verification</span>${sureBtns.join('')}` : ''}`;

    const noticeDismissed = localStorage.getItem('swp-notice-dismissed') === '1';
    const noticeHtml = noticeDismissed ? '' : `
        <div class="swp-pane-notice" id="swp-pane-notice">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <span>Step 1 = current state. Set the total count, then add a row per Veeam job currently protecting them.</span>
            <button type="button" class="swp-notice-dismiss" data-swp-dismiss-notice aria-label="Dismiss">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M1 1l8 8M9 1L1 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
            </button>
        </div>`;

    return `
        ${noticeHtml}
        <div class="swp-label-row">
            <svg class="swp-label-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            <input
                type="text"
                class="swp-label-input"
                placeholder="Custom name (optional)"
                value="${escapeHtml(workload.label || '')}"
                data-swp-label
                maxlength="60"
                aria-label="Custom label (optional)"
                spellcheck="false"
                autocomplete="off"
            >
        </div>
        <div class="swp-total-count-row">
            <label class="swp-total-count-label" for="swp-total-count">Total ${escapeHtml(unit)}</label>
            <div class="swp-stepper">
                <button type="button" class="swp-stepper-btn" data-swp-decrement aria-label="Decrease count">−</button>
                <input
                    type="number"
                    id="swp-total-count"
                    class="swp-count-input swp-count-input--total"
                    min="0"
                    value="${totalCount}"
                    data-swp-total-count
                    aria-label="Total ${escapeHtml(unit)}"
                >
                <button type="button" class="swp-stepper-btn" data-swp-increment aria-label="Increase count">+</button>
            </div>
        </div>
        <div class="swp-jobs-section">
            <div class="swp-jobs-section-hdr">
                <span class="swp-section-label">Current Veeam jobs</span>
            </div>
            <div class="swp-jobs-list" id="swp-jobs-list">
                ${jobsHtml}
                ${emptyHint}
            </div>
            <div class="swp-type-picker">
                <span class="swp-type-picker-label">+ Add job</span>
                <div class="swp-type-picker-btns">${typePicker}</div>
            </div>
        </div>
    `;
}

// ── Job card ──────────────────────────────────────────────────────────────────

function _buildJobCardMarkup(job, index, totalCount, servers, allJobs = [], isVb365 = false, isVbfCloud = false) {
    const m     = _JOB_META[job.type] || { label: job.type, cls: 'backup', short: '?' };
    const count = parseInt(job.count, 10) || 0;

    // SureBackup/SureReplica are capped by the count of their parent job type
    const _parentCount = (parentType) => {
        const parent = allJobs.find((j) => j.type === parentType);
        return parent ? (parseInt(parent.count, 10) || 0) : totalCount;
    };
    const effectiveMax =
        job.type === 'sureBackup'  ? _parentCount('backup')      :
        job.type === 'sureReplica' ? _parentCount('replication')  :
        totalCount;

    const isSubset    = effectiveMax > 0 && count < effectiveMax;
    const isOverLimit = effectiveMax > 0 && count > effectiveMax;
    const barFillPct  = effectiveMax > 0 ? Math.round(Math.min(count, effectiveMax) / effectiveMax * 100) : 0;

    const unmanagedLabel = (isVb365 || isVbfCloud) ? '— no appliance —' : '— unmanaged —';
    const serverLabel    = isVb365 ? 'VB365 server' : isVbfCloud ? 'Appliance' : 'Server';
    const serverOptions  = [
        `<option value="">${unmanagedLabel}</option>`,
        ...servers.map((s) =>
            `<option value="${escapeHtml(s.instanceId)}" ${job.managedByServerId === s.instanceId ? 'selected' : ''}>${escapeHtml(s.name)}</option>`
        ),
    ].join('');

    const ofLabel = effectiveMax > 0
        ? (isOverLimit ? `max ${effectiveMax.toLocaleString()}` : `of ${effectiveMax.toLocaleString()}`)
        : '';

    return `
        <div class="swp-job-card swp-job-card--${m.cls}" data-swp-job-index="${index}">
            <div class="swp-job-card-hdr">
                <span class="swp-job-type-badge swp-job-type-badge--${m.cls}">${escapeHtml(m.label)}</span>
                <button type="button" class="swp-group-remove" data-swp-remove-job data-swp-job-index="${index}" aria-label="Remove ${escapeHtml(m.label)} job">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M1 1l8 8M9 1L1 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
                </button>
            </div>
            <div class="swp-job-count-row">
                <input
                    type="number"
                    class="swp-count-input swp-count-input--job${isOverLimit ? ' swp-count-input--overlimit' : ''}"
                    min="0"
                    max="${effectiveMax || ''}"
                    value="${count}"
                    data-swp-job-count
                    data-swp-job-index="${index}"
                    aria-label="${escapeHtml(m.label)} job — count"
                >
                <span class="swp-job-of-label${isOverLimit ? ' swp-job-of-label--overlimit' : ''}">${ofLabel}</span>
            </div>
            <div class="swp-job-bar-track swp-job-bar-track--${m.cls}"
                 data-swp-bar-track
                 data-swp-job-index="${index}"
                 data-swp-bar-max="${effectiveMax}"
                 role="slider"
                 tabindex="0"
                 aria-valuemin="0"
                 aria-valuemax="${effectiveMax}"
                 aria-valuenow="${count}"
                 aria-label="${escapeHtml(m.label)} count — drag or use arrow keys">
                <div class="swp-job-bar-fill" data-swp-bar-fill style="width:${barFillPct}%"></div>
            </div>
            <div class="swp-server-row">
                <label class="swp-server-label" for="swp-job-server-${index}">${serverLabel}</label>
                <select id="swp-job-server-${index}" class="swp-server-select" data-swp-job-server data-swp-job-index="${index}" aria-label="Managed by server">${serverOptions}</select>
            </div>
        </div>
    `;
}

// ── Icon helper ───────────────────────────────────────────────────────────────
// Inline SVG icons per workload type. Extend as workload types are added.
const _WORKLOAD_ICON_SVG = {
    'vmware-vsphere': `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><path d="M6 6h.01M6 18h.01"/></svg>`,
    'hyper-v':        `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><path d="M6 6h.01M6 18h.01"/></svg>`,
    'default':        `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>`,
};

function _buildWorkloadIconHtml(workloadValue) {
    return _WORKLOAD_ICON_SVG[workloadValue] || _WORKLOAD_ICON_SVG['default'];
}

// ── Site name helper ──────────────────────────────────────────────────────────
function _getSiteDisplayNameForPane(site) {
    const index = sites.findIndex((s) => s.id === site.id);
    return site.name || `Site ${index + 1}`;
}

// ── Active chip sync ──────────────────────────────────────────────────────────
/** Adds/removes the `swp-chip--active` class on all source workload chips. */
function _syncActiveChipState() {
    document.querySelectorAll('[data-swp-chip]').forEach((chip) => {
        const chipSiteId     = parseInt(chip.dataset.swpSiteId,     10);
        const chipWorkloadId = parseInt(chip.dataset.swpWorkloadId, 10);
        const isActive       = chipSiteId === swPaneSiteId && chipWorkloadId === swPaneWorkloadId;
        chip.classList.toggle('swp-chip--active', isActive);
    });
}

// ── Mutation helpers ──────────────────────────────────────────────────────────

/** Removes jobs with types that aren't valid for this workload type. */
function _sanitiseWorkloadProtection(workload) {
    if (!Array.isArray(workload.jobs)) return;
    const wv            = workload.workloadValue;
    const hasBackupJob  = workload.jobs.some((j) => j.type === 'backup');
    const hasReplJob    = workload.jobs.some((j) => j.type === 'replication');
    workload.jobs = workload.jobs.filter((j) => {
        if (j.type === 'replication'  && !replicationAllowedWorkloads.has(wv)) return false;
        if (j.type === 'cdp'          && !cdpAllowedWorkloads.has(wv))         return false;
        if (j.type === 'backupCopy'   && backupCopyExcludedWorkloads.has(wv))  return false;
        if (j.type === 'sureBackup'   && (!sureBackupAllowedWorkloads.has(wv) || !hasBackupJob)) return false;
        if (j.type === 'sureReplica'  && (!replicationAllowedWorkloads.has(wv) || !hasReplJob))  return false;
        return true;
    });
}

function _getActiveWorkload() {
    if (!swPaneSiteId || !swPaneWorkloadId) return null;
    const site = getSiteById(swPaneSiteId);
    if (!site) return null;
    return { site, workload: (site.sourceWorkloads || []).find((w) => w.id === swPaneWorkloadId) || null };
}

// ── Bar drag helpers ──────────────────────────────────────────────────────────

/** Syncs bar fill + aria-valuenow for a given job index without re-rendering. */
function _updateBarFillInDom(jobIndex, value, max) {
    const track = paneBodyEl?.querySelector(`[data-swp-bar-track][data-swp-job-index="${jobIndex}"]`);
    if (!track) return;
    const fill = track.querySelector('[data-swp-bar-fill]');
    if (fill) fill.style.width = (max > 0 ? Math.round(Math.min(value, max) / max * 100) : 0) + '%';
    track.setAttribute('aria-valuenow', value);
}

/** Applies a count value from bar interaction: updates model, bar fill, and number input. */
function _applyBarValue(jobIndex, value) {
    const result = _getActiveWorkload();
    if (!result?.workload) return;
    const job = result.workload.jobs?.[jobIndex];
    if (!job) return;

    job.count = Math.max(0, value);

    // Clamp sure-type dependents in model (DOM update deferred to rerender on drag end)
    if (job.type === 'backup') {
        result.workload.jobs.filter((j) => j.type === 'sureBackup'  && j.count > job.count)
            .forEach((j) => { j.count = job.count; });
        if (_barDrag) _barDrag.needsRerender = true;
    }
    if (job.type === 'replication') {
        result.workload.jobs.filter((j) => j.type === 'sureReplica' && j.count > job.count)
            .forEach((j) => { j.count = job.count; });
        if (_barDrag) _barDrag.needsRerender = true;
    }

    // Update bar fill
    const track = _barDrag?.track || paneBodyEl?.querySelector(`[data-swp-bar-track][data-swp-job-index="${jobIndex}"]`);
    const max   = track ? (parseInt(track.dataset.swpBarMax, 10) || 0) : 0;
    _updateBarFillInDom(jobIndex, job.count, max);

    // Update number input (skip if that input currently has focus — user is typing)
    const card  = paneBodyEl?.querySelector(`.swp-job-card[data-swp-job-index="${jobIndex}"]`);
    const input = card?.querySelector('[data-swp-job-count]');
    if (input && document.activeElement !== input) input.value = job.count;

    _notifyChange();
}

/** Called on drag end: flush rerender + chip detail update. */
function _endBarDrag() {
    if (!_barDrag) return;
    const { needsRerender } = _barDrag;
    _barDrag = null;
    if (needsRerender) {
        _rerenderPaneBody();
    }
    const result = _getActiveWorkload();
    if (result?.workload) {
        _updateChipDetail(
            document.querySelector(`[data-swp-chip][data-swp-site-id="${swPaneSiteId}"][data-swp-workload-id="${swPaneWorkloadId}"]`),
            result.workload
        );
    }
}


// ── Event wiring ──────────────────────────────────────────────────────────────

let _eventsWired = false;

export function initSourceWorkloadPane() {
    paneEl      = document.getElementById('swp-pane');
    paneBodyEl  = document.getElementById('swp-pane-body');
    paneTitleEl = document.getElementById('swp-pane-title');
    paneSubEl   = document.getElementById('swp-pane-subtitle');
    paneIconEl  = document.getElementById('swp-pane-icon');

    if (!paneEl || _eventsWired) return;
    _eventsWired = true;

    // ── Delegated events on pane ──────────────────────────────────────────────

    paneEl.addEventListener('click', (e) => {
        // Stop propagation so clicks inside the pane never reach the document-level
        // click-outside handler — even after a re-render detaches e.target from the DOM.
        e.stopPropagation();

        if (e.target.closest('[data-swp-close]')) { closeSourceWorkloadPane(); return; }

        if (e.target.closest('[data-swp-dismiss-notice]')) {
            localStorage.setItem('swp-notice-dismissed', '1');
            const notice = document.getElementById('swp-pane-notice');
            if (notice) notice.remove();
            return;
        }

        if (e.target.closest('[data-swp-decrement]')) { _handleStepperClick(-1); return; }
        if (e.target.closest('[data-swp-increment]')) { _handleStepperClick(1);  return; }

        const removeBtn = e.target.closest('[data-swp-remove-job]');
        if (removeBtn) { _handleRemoveJob(parseInt(removeBtn.dataset.swpJobIndex, 10)); return; }

        const addBtn = e.target.closest('[data-swp-add-job-type]');
        if (addBtn && !addBtn.disabled) { _handleAddJob(addBtn.dataset.swpAddJobType); return; }
    });

    paneEl.addEventListener('input', (e) => {
        if (e.target.closest('[data-swp-label]'))       { _handleLabelChange(e.target);       return; }
        if (e.target.closest('[data-swp-total-count]')) { _handleTotalCountChange(e.target);  return; }
        if (e.target.closest('[data-swp-job-count]'))   { _handleJobCountChange(e.target);    return; }
    });

    paneEl.addEventListener('change', (e) => {
        if (e.target.closest('[data-swp-job-server]')) { _handleJobServerChange(e.target); return; }
    });

    // ── Bar drag ──────────────────────────────────────────────────────────────

    // Mousedown on bar track — start drag
    paneEl.addEventListener('mousedown', (e) => {
        const track = e.target.closest('[data-swp-bar-track]');
        if (!track) return;
        e.preventDefault(); // prevent text selection while dragging
        const jobIndex = parseInt(track.dataset.swpJobIndex, 10);
        _barDrag = { track, jobIndex, needsRerender: false };
        const rect  = track.getBoundingClientRect();
        const max   = parseInt(track.dataset.swpBarMax, 10) || 0;
        _applyBarValue(jobIndex, Math.round(Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) * max));
    });

    // Touchstart on bar track
    paneEl.addEventListener('touchstart', (e) => {
        const track = e.target.closest('[data-swp-bar-track]');
        if (!track) return;
        const jobIndex = parseInt(track.dataset.swpJobIndex, 10);
        _barDrag = { track, jobIndex, needsRerender: false };
        const rect = track.getBoundingClientRect();
        const max  = parseInt(track.dataset.swpBarMax, 10) || 0;
        _applyBarValue(jobIndex, Math.round(Math.min(1, Math.max(0, (e.touches[0].clientX - rect.left) / rect.width)) * max));
    }, { passive: true });

    // Arrow-key control when bar track is focused
    paneEl.addEventListener('keydown', (e) => {
        const track = e.target.closest('[data-swp-bar-track]');
        if (!track) return;
        const max      = parseInt(track.dataset.swpBarMax, 10) || 0;
        const jobIndex = parseInt(track.dataset.swpJobIndex, 10);
        if (max === 0) return;
        const result = _getActiveWorkload();
        if (!result?.workload) return;
        const job = result.workload.jobs?.[jobIndex];
        if (!job) return;
        const step = Math.max(1, Math.round(max / 20));
        let newVal = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp')  { newVal = Math.min(max, job.count + step); e.preventDefault(); }
        if (e.key === 'ArrowLeft'  || e.key === 'ArrowDown') { newVal = Math.max(0, job.count - step);  e.preventDefault(); }
        if (e.key === 'Home') { newVal = 0;   e.preventDefault(); }
        if (e.key === 'End')  { newVal = max; e.preventDefault(); }
        if (newVal === null) return;
        _applyBarValue(jobIndex, newVal);
        // Keyboard adjustments on backup/replication need an immediate rerender for sure-type hints
        const needsRerender = job.type === 'backup' || job.type === 'replication';
        if (needsRerender) _rerenderPaneBody();
    });

    // Document-level: drag move
    document.addEventListener('mousemove', (e) => {
        if (!_barDrag) return;
        const rect = _barDrag.track.getBoundingClientRect();
        const max  = parseInt(_barDrag.track.dataset.swpBarMax, 10) || 0;
        _applyBarValue(_barDrag.jobIndex, Math.round(Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) * max));
    });
    document.addEventListener('touchmove', (e) => {
        if (!_barDrag) return;
        const rect = _barDrag.track.getBoundingClientRect();
        const max  = parseInt(_barDrag.track.dataset.swpBarMax, 10) || 0;
        _applyBarValue(_barDrag.jobIndex, Math.round(Math.min(1, Math.max(0, (e.touches[0].clientX - rect.left) / rect.width)) * max));
    }, { passive: true });

    // Document-level: drag end
    document.addEventListener('mouseup',  _endBarDrag);
    document.addEventListener('touchend', _endBarDrag);

    // Escape key closes pane
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isSourceWorkloadPaneOpen()) {
            closeSourceWorkloadPane();
        }
    });

    // Click-outside: close the pane when clicking outside both the pane and the chip that opened it.
    document.addEventListener('click', (e) => {
        if (!isSourceWorkloadPaneOpen()) return;
        // Ignore clicks that happened within 200 ms of the pane opening (prevents same-click close).
        if (Date.now() - _paneOpenedAt < 200) return;
        // Clicks inside the pane itself are fine.
        // Use closest() instead of contains() so re-rendered (detached) targets still match.
        if (e.target.closest('#swp-pane')) return;
        // Clicks on a workload chip are handled by the chip handler (toggles / switches pane).
        if (e.target.closest('[data-swp-chip]')) return;
        closeSourceWorkloadPane();
    }, { capture: false });

    // Window bridges
    window.closeSourceWorkloadPane  = closeSourceWorkloadPane;
    window.isSourceWorkloadPaneOpen = isSourceWorkloadPaneOpen;
    window.openSourceWorkloadPane   = openSourceWorkloadPane;
}

// ── Event handlers ────────────────────────────────────────────────────────────

function _handleLabelChange(input) {
    const result = _getActiveWorkload();
    if (!result?.workload) return;
    result.workload.label = input.value;
    // Update chip label live without full re-render
    const chip = document.querySelector(
        `[data-swp-chip][data-swp-site-id="${swPaneSiteId}"][data-swp-workload-id="${swPaneWorkloadId}"]`
    );
    if (chip) {
        const labelEl = chip.querySelector('.swp-chip-label');
        if (labelEl) {
            const defaultLabel = chip.dataset.swpDefaultLabel || '';
            labelEl.textContent = input.value.trim() || defaultLabel;
        }
    }
    _notifyChange();
}

function _handleStepperClick(delta) {
    const result = _getActiveWorkload();
    if (!result?.workload) return;
    const current = parseInt(result.workload.totalCount, 10) || 0;
    result.workload.totalCount = Math.max(0, current + delta);
    // Sync the visible input
    const input = document.getElementById('swp-total-count');
    if (input) input.value = result.workload.totalCount;
    _updateChipCount(result.workload);
    _notifyChange();
}

function _handleTotalCountChange(input) {
    const result = _getActiveWorkload();
    if (!result?.workload) return;
    result.workload.totalCount = Math.max(0, parseInt(input.value, 10) || 0);
    _updateChipCount(result.workload);
    _notifyChange();
}

function _handleJobCountChange(input) {
    const jobIndex = parseInt(input.dataset.swpJobIndex, 10);
    const result   = _getActiveWorkload();
    if (!result?.workload) return;
    const job = result.workload.jobs?.[jobIndex];
    if (!job) return;

    job.count = Math.max(0, parseInt(input.value, 10) || 0);

    // Clamp dependent sure-type jobs if parent count decreased
    const _clampSure = (sureType, parentCount) => {
        result.workload.jobs
            .filter((j) => j.type === sureType && j.count > parentCount)
            .forEach((j) => { j.count = parentCount; });
    };
    if (job.type === 'backup')      _clampSure('sureBackup',  job.count);
    if (job.type === 'replication') _clampSure('sureReplica', job.count);

    // Re-render pane if any sure jobs were clamped (count changed in model)
    const needsRerender = job.type === 'backup' || job.type === 'replication';
    if (needsRerender) {
        _rerenderPaneBody();
    } else {
        // No full rerender — sync bar fill directly
        const track = paneBodyEl?.querySelector(`[data-swp-bar-track][data-swp-job-index="${jobIndex}"]`);
        const max   = track ? (parseInt(track.dataset.swpBarMax, 10) || 0) : 0;
        _updateBarFillInDom(jobIndex, job.count, max);
    }

    _updateChipDetail(
        document.querySelector(`[data-swp-chip][data-swp-site-id="${swPaneSiteId}"][data-swp-workload-id="${swPaneWorkloadId}"]`),
        result.workload
    );
    _notifyChange();
}

function _handleJobServerChange(select) {
    const jobIndex = parseInt(select.dataset.swpJobIndex, 10);
    const result   = _getActiveWorkload();
    if (!result?.workload) return;
    const job = result.workload.jobs?.[jobIndex];
    if (!job) return;
    job.managedByServerId = select.value || null;
    _notifyChange();
}

function _handleAddJob(type) {
    const result = _getActiveWorkload();
    if (!result?.workload) return;
    const defaultCount = parseInt(result.workload.totalCount, 10) || 0;
    result.workload.jobs.push(buildDefaultSourceWorkloadJob(type, defaultCount));
    _rerenderPaneBody();
    _updateChipProtection(result.workload);
    _notifyChange();
}

function _handleRemoveJob(jobIndex) {
    const result = _getActiveWorkload();
    if (!result?.workload) return;
    result.workload.jobs.splice(jobIndex, 1);
    _rerenderPaneBody();
    _updateChipProtection(result.workload);
    _notifyChange();
}

// ── Chip face update ──────────────────────────────────────────────────────────
/**
 * Updates the data-protected attribute on the active chip so the green/blue
 * left-border colour switches immediately when protection is toggled in the pane,
 * without waiting for a full renderSiteList.
 */
function _updateChipProtection(workload) {
    if (!swPaneSiteId || !swPaneWorkloadId) return;
    const chip = document.querySelector(
        `[data-swp-chip][data-swp-site-id="${swPaneSiteId}"][data-swp-workload-id="${swPaneWorkloadId}"]`
    );
    if (!chip) return;
    const hasProtection = Array.isArray(workload.jobs) && workload.jobs.length > 0;
    chip.toggleAttribute('data-protected', hasProtection);
    // Refresh badge icons in the chip summary row
    const badgesEl = chip.querySelector('.swp-chip-badges');
    if (badgesEl) badgesEl.innerHTML = buildSwpChipBadges(workload);
    // Refresh the expandable detail panel
    _updateChipDetail(chip, workload);
}

/**
 * Rebuilds the inline detail panel inside a chip with current workload state.
 * Called after any protection or count change so the expanded view stays in sync.
 */
function _updateChipDetail(chip, workload) {
    if (!chip) return;
    const existing = chip.querySelector('.swp-chip-detail');
    const newHtml  = buildSwpChipDetail(workload);
    if (existing) {
        existing.outerHTML = newHtml || '';
    } else if (newHtml) {
        chip.querySelector('.swp-chip-main')?.insertAdjacentHTML('afterend', newHtml);
    }
}

/**
 * Updates just the count label on the active source workload chip
 * without triggering a full renderSiteList. Called on every count keystroke.
 */
function _updateChipCount(workload) {
    if (!swPaneSiteId || !swPaneWorkloadId) return;
    const chip = document.querySelector(
        `[data-swp-chip][data-swp-site-id="${swPaneSiteId}"][data-swp-workload-id="${swPaneWorkloadId}"]`
    );
    if (!chip) return;
    const totalCount = parseInt(workload.totalCount, 10) || 0;
    chip.toggleAttribute('data-count-zero', totalCount === 0);
    // Re-render detail panel (owns the count display now)
    _updateChipDetail(chip, workload);
}

// ── Notify data change ────────────────────────────────────────────────────────
function _notifyChange() {
    // In Phase 3, SITE_LIST_CHANGED will be added here to refresh chip badges.
    // For now, only trigger estimate recalc.
    emit(BUS_EVENTS.ESTIMATE_NEEDED);
}
